import React, { useState, useEffect } from 'react';
import PageHeader from '../../components/PageHeader';
import PageTransition from '../../components/PageTransition';
import { motion } from 'framer-motion';
import { fetchExchangeRates } from '../../services/currencyService';
import countryConfig from '../../config/country.json';

// 处理货币配置，去重并格式化
const currencies = Array.from(new Set(countryConfig.map(country => country.currency_code)))
  .map(code => {
    const country = countryConfig.find(c => c.currency_code === code);
    return {
      code,
      symbol: country.currency_symbol,
      name: country.currency_chinese_name
    };
  });

function ExchangePage() {
  const [amount, setAmount] = useState('');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('CNY');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState(null);
  const [error, setError] = useState(null);

  // 获取汇率数据
  useEffect(() => {
    const loadRates = async () => {
      try {
        setLoading(true);
        const ratesData = await fetchExchangeRates();
        setRates(ratesData);
        setError(null);
      } catch (err) {
        console.error('获取汇率失败:', err);
        setError('获取汇率数据失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    loadRates();
  }, []);

  // 计算汇率和结果
  const calculateExchange = (value, from, to) => {
    if (!rates || !value) return null;

    const fromRate = rates[`USD${from}`] || 1;
    const toRate = rates[`USD${to}`] || 1;
    
    // 先转换为 USD，再转换为目标货币
    const usdAmount = value / fromRate;
    const result = usdAmount * toRate;

    return {
      amount: parseFloat(value),
      from,
      to,
      rate: toRate / fromRate,
      result
    };
  };

  // 处理汇率转换
  useEffect(() => {
    if (rates) {
      const result = calculateExchange(amount || '1', fromCurrency, toCurrency);
      setResult(result);
    }
  }, [amount, fromCurrency, toCurrency, rates]);

  const handleSwapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const handleRateClick = (targetCurrency) => {
    setFromCurrency(fromCurrency);
    setToCurrency(targetCurrency);
  };

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.symbol : '';
  };

  if (error) {
    return (
      <PageTransition className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <PageHeader
          title="汇率助手"
          subtitle="快速进行货币换算"
        />
        <div className="container mx-auto px-4 py-6">
          <div className="text-center text-red-600 p-4 rounded-lg bg-red-50">
            {error}
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <PageHeader
        title="汇率助手"
        subtitle="快速进行货币换算"
      />
      <div className='bg-white p-2 sticky top-14 z-10 shadow-lg'>
      <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-2"
        >
          {/* 转换结果显示 */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-2 p-2 bg-gradient-to-br from-indigo-500/5 to-blue-500/5 rounded-2xl relative overflow-hidden"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-2 mb-1">
                <div className="text-xl font-semibold text-gray-800">
                  {getCurrencySymbol(fromCurrency)}{result?.amount || '1'}
                </div>
                <div className="text-base text-gray-500">{fromCurrency}</div>
              </div>
              <div className="flex items-center justify-center gap-1 mb-1">
                <div className="h-[1px] w-8 bg-gray-300"></div>
                <div className="rounded-full bg-indigo-50 p-1.5">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <div className="h-[1px] w-8 bg-gray-300"></div>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600">
                  {getCurrencySymbol(toCurrency)}{result?.result.toFixed(2)}
                </div>
                <div className="text-base text-gray-500">{toCurrency}</div>
              </div>
              <div className="text-center text-xs text-gray-500 mt-1 font-medium">
                汇率：1 {fromCurrency} = {result?.rate.toFixed(4)} {toCurrency}
              </div>
            </div>
          </motion.div>

          <div className="space-y-2">
            {/* 金额输入 */}
            <div className="relative">
              <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <span className="text-gray-500 text-base">{getCurrencySymbol(fromCurrency)}</span>
              </div>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-12 pr-4 py-2 rounded-xl border-2 border-gray-200 bg-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base font-medium"
                placeholder="输入金额"
                autoFocus
              />
            </div>

            {/* 货币选择 */}
            <div className="flex items-center justify-center gap-3">
              <div>
                <select
                  value={fromCurrency}
                  onChange={(e) => setFromCurrency(e.target.value)}
                  className="w-full px-1 py-2 rounded-xl border-2 border-gray-200 bg-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm font-medium"
                >
                  {currencies.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.name} ({currency.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* 交换按钮 */}
              <motion.button
                onClick={handleSwapCurrencies}
                className=" w-8 h-8 rounded-full flex items-center justify-center "
              >
                <svg className="w-4 h-4 rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              </motion.button>

              <div>
                <select
                  value={toCurrency}
                  onChange={(e) => setToCurrency(e.target.value)}
                  className="w-full px-1 py-2 rounded-xl border-2 border-gray-200 bg-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm font-medium"
                >
                  {currencies.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.name} ({currency.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      <div className="container mx-auto px-4">


        {/* 常用汇率列表 */}
        <div className="mt-4 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 px-2">常用汇率</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {currencies.filter(c => c.code !== fromCurrency).map((currency) => {
              const rate = rates ? (rates[`USD${fromCurrency}`] / rates[`USD${currency.code}`]).toFixed(4) : '加载中...';
              return (
                <motion.button
                  key={currency.code}
                  onClick={() => handleRateClick(currency.code)}
                  className="backdrop-blur-xl bg-white/70 rounded-xl shadow-lg p-4 border border-white/20 w-full text-left hover:bg-white/80 transition-colors group"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-500">
                        1 {fromCurrency} = {rate} {currency.code}
                      </div>
                    </div>
                    <div className="text-indigo-600 flex items-center gap-1">
                      <span className='text-xs'>{currency.name}</span>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </PageTransition>
  );
}

export default ExchangePage; 